/**
 * CadenceControls — the recurring cadence editor (#87 / Detail Card "How often").
 * None / Daily (times a day) / Weekly (which days) / Monthly (which date), with a
 * plain-English summary of the payable units. Controlled: `value` is
 * { cadence, unit_rate, days_of_week, times_per_day, expected_day }; emits the
 * same shape via onChange. Backend turns it into the month's count.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useMemo } from 'react';

const DAYS = [['1', 'Mon'], ['2', 'Tue'], ['3', 'Wed'], ['4', 'Thu'], ['5', 'Fri'], ['6', 'Sat'], ['7', 'Sun']];
const CAD = [['none', 'One-off'], ['daily', 'Daily'], ['weekly', 'Weekly'], ['monthly', 'Monthly']];

export default function CadenceControls({ value = {}, onChange, hideRate = false }) {
  const v = { cadence: 'none', unit_rate: '', days_of_week: '', times_per_day: 1, expected_day: '', ...value };
  const set = (patch) => onChange?.({ ...v, ...patch });
  const days = (v.days_of_week || '').split(',').filter(Boolean);
  const toggleDay = (d) => {
    const next = days.includes(d) ? days.filter((x) => x !== d) : [...days, d];
    set({ days_of_week: next.sort().join(',') });
  };
  const summary = useMemo(() => {
    const rate = Number(v.unit_rate) || 0;
    if (v.cadence === 'none' || !rate) return null;
    const per = Math.max(1, Number(v.times_per_day) || 1);
    if (v.cadence === 'daily') return `R${rate} × ~30 days${per > 1 ? ` × ${per}/day` : ''} ≈ R${rate * 30 * per}/month.`;
    if (v.cadence === 'monthly') return `R${rate} once a month${v.expected_day ? ` on day ${v.expected_day}` : ''}.`;
    const names = days.map((d) => DAYS.find((x) => x[0] === d)?.[1]).filter(Boolean);
    return names.length ? `R${rate} on ${names.join(', ')} — ~${names.length * 4 * per} units/month.` : 'Pick which days.';
  }, [v.cadence, v.unit_rate, v.times_per_day, v.expected_day, v.days_of_week]);

  return (
    <div className="space-y-2">
      <div className="text-[11px] text-slate-500">How often</div>
      <div className="flex gap-2 flex-wrap">
        {CAD.map(([k, l]) => (
          <button key={k} type="button"
                  className={`rounded-lg border px-3 py-1.5 text-sm transition ${v.cadence === k ? 'border-vblue text-vblue bg-vblue/10' : 'border-edge text-slate-300 hover:border-slate-500'}`}
                  onClick={() => set({ cadence: k })}>{l}</button>
        ))}
      </div>
      {v.cadence !== 'none' && !hideRate && (
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500">R</span>
          <input className="input !w-28 !py-1.5 text-sm" inputMode="decimal" placeholder="per unit"
                 value={v.unit_rate} onChange={(e) => set({ unit_rate: e.target.value })} />
          <span className="text-[11px] text-slate-500">each</span>
        </div>
      )}
      {v.cadence === 'daily' && (
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500">Times a day</span>
          <button type="button" className="w-7 h-7 rounded-full border border-edge text-slate-300" onClick={() => set({ times_per_day: Math.max(1, (Number(v.times_per_day) || 1) - 1) })}>−</button>
          <span className="text-sm w-6 text-center">{v.times_per_day || 1}</span>
          <button type="button" className="w-7 h-7 rounded-full border border-vblue text-vblue" onClick={() => set({ times_per_day: (Number(v.times_per_day) || 1) + 1 })}>+</button>
        </div>
      )}
      {v.cadence === 'weekly' && (
        <div>
          <div className="text-[11px] text-slate-500 mb-1">Which days?</div>
          <div className="flex gap-1 flex-wrap">
            {DAYS.map(([d, l]) => (
              <button key={d} type="button"
                      className={`rounded-lg border px-2.5 py-1 text-xs transition ${days.includes(d) ? 'border-vblue text-vblue bg-vblue/10' : 'border-edge text-slate-400 hover:border-slate-500'}`}
                      onClick={() => toggleDay(d)}>{l}</button>
            ))}
          </div>
        </div>
      )}
      {v.cadence === 'monthly' && (
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500">Which day of the month?</span>
          <input className="input !w-20 !py-1.5 text-sm" type="number" min="1" max="28"
                 value={v.expected_day} placeholder="1–28" onChange={(e) => set({ expected_day: e.target.value })} />
        </div>
      )}
      {summary && <p className="text-[11px]" style={{ color: 'var(--color-accent-400, #a99ee4)' }}>{summary}</p>}
    </div>
  );
}
